import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export default function PressReleaseDetail() {
  const { id } = useParams();
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${API_URL}/api/press-releases/show.json?id=${id}`)
      .then((res) => {
        setPr(res.data.pressRelease);
        setLoading(false);
      })
      .catch(() => {
        setError('Press release not found.');
        setLoading(false);
      });
  }, [id]);

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }

  if (loading) return <div style={styles.page}><div style={styles.centered}>Loading...</div></div>;
  if (error || !pr) return <div style={styles.page}><div style={styles.centered}>{error || 'Not found.'} <Link to="/">← Back</Link></div></div>;

  const tags = pr.tags ? pr.tags.split(',').map((t) => t.trim()).filter(Boolean) : [];

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Link to="/" style={styles.backLink}>← Back to Newsroom</Link>

        <article style={styles.article}>
          <header style={styles.header}>
            {pr.category && <span style={styles.categoryBadge}>{pr.category}</span>}
            <h1 style={styles.headline}>{pr.headline}</h1>
            {pr.summary && <p style={styles.summary}>{pr.summary}</p>}
            <div style={styles.meta}>
              <span>By <strong>{pr.authorName}</strong></span>
              <span style={styles.metaDot}>·</span>
              <span>{formatDate(pr.createdAt)}</span>
              {pr.websiteUrl && (
                <>
                  <span style={styles.metaDot}>·</span>
                  <a href={pr.websiteUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    {pr.websiteUrl.replace(/^https?:\/\//, '')}
                  </a>
                </>
              )}
            </div>
            {tags.length > 0 && (
              <div style={styles.tags}>
                {tags.map((tag) => <span key={tag} style={styles.tag}>{tag}</span>)}
              </div>
            )}
          </header>

          <div style={styles.body}>
            {pr.body.split('\n').map((para, i) =>
              para.trim() ? <p key={i} style={styles.para}>{para}</p> : <br key={i} />
            )}
          </div>

          {pr.reportFile && (
            <div style={styles.reportBox}>
              <span style={styles.reportIcon}>📄</span>
              <div>
                <div style={styles.reportLabel}>Distribution Report Available</div>
                <a
                  href={pr.reportFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.reportDownload}
                >
                  Download Report PDF →
                </a>
              </div>
            </div>
          )}

          {(pr.contactPerson || pr.email || pr.phone) && (
            <div style={styles.contactBox}>
              <h3 style={styles.contactHeading}>Media Contact</h3>
              {pr.contactPerson && <p style={styles.contactLine}><strong>{pr.contactPerson}</strong></p>}
              {pr.email && (
                <p style={styles.contactLine}>
                  <a href={`mailto:${pr.email}`} style={styles.link}>{pr.email}</a>
                </p>
              )}
              {pr.phone && <p style={styles.contactLine}>{pr.phone}</p>}
            </div>
          )}
        </article>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f8f9fa', fontFamily: 'system-ui, sans-serif', padding: '2rem 1rem' },
  centered: { textAlign: 'center', padding: '4rem', color: '#666' },
  container: { maxWidth: '800px', margin: '0 auto' },
  backLink: { color: '#888', textDecoration: 'none', fontSize: '0.9rem', display: 'inline-block', marginBottom: '1.5rem' },
  article: { background: '#fff', borderRadius: '10px', padding: '2.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  header: { borderBottom: '1px solid #f0f0f0', paddingBottom: '1.5rem', marginBottom: '2rem' },
  categoryBadge: {
    display: 'inline-block',
    background: '#e94560',
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 700,
    padding: '0.2rem 0.7rem',
    borderRadius: '20px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
  },
  headline: { margin: '0 0 0.75rem', fontSize: '2rem', lineHeight: 1.3, color: '#1a1a2e' },
  summary: { margin: '0 0 1rem', fontSize: '1.1rem', color: '#555', lineHeight: 1.6, fontStyle: 'italic' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#888', fontSize: '0.85rem', flexWrap: 'wrap' },
  metaDot: { color: '#ccc' },
  link: { color: '#e94560', textDecoration: 'none' },
  tags: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.75rem' },
  tag: {
    background: '#f0f0f0',
    color: '#555',
    fontSize: '0.75rem',
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
  },
  body: { color: '#333', lineHeight: 1.8, fontSize: '1rem' },
  para: { margin: '0 0 1.2rem' },
  contactBox: {
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #f0f0f0',
  },
  contactHeading: { margin: '0 0 0.75rem', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999' },
  contactLine: { margin: '0 0 0.25rem', fontSize: '0.95rem', color: '#444' },
  reportBox: { display: 'flex', alignItems: 'center', gap: '1rem', background: '#f0f7ff', border: '1px solid #bcd4f0', borderRadius: 8, padding: '1rem 1.25rem', marginTop: '1.5rem', marginBottom: '0.5rem' },
  reportIcon: { fontSize: '2rem' },
  reportLabel: { fontSize: '0.82rem', color: '#555', marginBottom: '0.25rem' },
  reportDownload: { fontSize: '0.95rem', fontWeight: 700, color: '#1565c0', textDecoration: 'none' },
};
